import type { SourceTypeDefinition } from '../../../core/definitions';
import type { BlendMode, Id, ScaleFilter } from '../../../core/model';
import type { StudioApi } from '../../../engine/api';
import type { Actions } from '../../actions';
import { tidyMenu, type MenuEntry } from '../../components/menuModel';
import { attempt } from '../../safe';
import { isDiagnosticsMode, type UiStore } from '../../uiStore';

const BLEND_MODES: readonly BlendMode[] = ['normal', 'additive', 'subtract', 'screen', 'multiply', 'lighten', 'darken'];
const SCALE_FILTERS: readonly ScaleFilter[] = ['default', 'point', 'bilinear', 'bicubic', 'lanczos', 'area'];
const CATEGORY_ORDER: readonly NonNullable<SourceTypeDefinition['category']>[] = [
  'capture', 'media', 'graphics', 'network', 'utility', 'plugin',
];

const title = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export function transformMenu(actions: Actions): MenuEntry[] {
  return [
    { label: 'Edit transform…', onSelect: () => actions.openTransform() },
    { label: 'Reset transform', onSelect: () => actions.fit('reset') },
    { separator: true },
    { label: 'Fit to screen', onSelect: () => actions.fit('fit') },
    { label: 'Stretch to screen', onSelect: () => actions.fit('stretch') },
    { label: 'Center on screen', onSelect: () => actions.fit('center') },
    { label: 'Center horizontally', onSelect: () => actions.fit('center-horizontal') },
    { label: 'Center vertically', onSelect: () => actions.fit('center-vertical') },
    { separator: true },
    { label: 'Rotate 90° clockwise', onSelect: () => actions.rotate(90) },
    { label: 'Rotate 90° counter-clockwise', onSelect: () => actions.rotate(-90) },
    { label: 'Rotate 180°', onSelect: () => actions.rotate(180) },
    { separator: true },
    { label: 'Flip horizontal', onSelect: () => actions.flip('horizontal') },
    { label: 'Flip vertical', onSelect: () => actions.flip('vertical') },
  ];
}

function groupEntries(studio: StudioApi, ui: UiStore, actions: Actions): MenuEntry[] {
  const sceneId = actions.editedSceneId();
  return [
    { label: 'Group selected items', disabled: !sceneId || studio.queries.groupIssue(studio.project.getState().project, sceneId, actions.selectedIds()) !== null, onSelect: () => actions.group() },
    {
      label: 'Add empty group',
      disabled: !sceneId,
      onSelect: () => {
        const groupId = sceneId ? attempt(studio, () => studio.commands.groupItems(sceneId, [])) : undefined;
        if (groupId) ui.getState().select([groupId]);
      },
    },
  ];
}

/** Source types grouped by category, each opening the create-new / add-existing dialog. */
export function addSourceMenu(studio: StudioApi, ui: UiStore, actions: Actions): MenuEntry[] {
  // Diagnostic fixtures are registered and loadable but only offered in diagnostics mode.
  const diagnostics = isDiagnosticsMode(ui.getState());
  const defs = studio.registry.listSources().filter((def) => diagnostics || !def.diagnostic);
  const entries: (MenuEntry | null)[] = [];
  const groups = new Map<string, SourceTypeDefinition[]>();
  for (const def of defs) {
    const key = def.category ?? 'utility';
    const list = groups.get(key);
    if (list) list.push(def);
    else groups.set(key, [def]);
  }
  for (const category of CATEGORY_ORDER) {
    const list = groups.get(category);
    if (!list) continue;
    for (const def of [...list].sort((a, b) => a.displayName.localeCompare(b.displayName))) {
      entries.push({ label: def.displayName, onSelect: () => ui.getState().openDialog({ type: 'add-source', kind: def.kind }) });
    }
    entries.push({ separator: true });
  }

  const sceneId = actions.editedSceneId();
  const project = studio.project.getState().project;
  // Every other scene is listed; the ones that would contain themselves are shown disabled and say why.
  const others = project.sceneOrder.filter((id) => id !== sceneId);
  const wouldLoop = (id: Id): boolean => studio.queries.reachableScenes(project, id).has(sceneId ?? '');
  entries.push({
    label: 'Scene',
    disabled: others.length === 0 || !sceneId,
    submenu: others.map((id) => {
      const loops = wouldLoop(id);
      return {
        label: `${project.scenes[id]?.name ?? id}${loops ? ' (would contain itself)' : ''}`,
        disabled: loops,
        onSelect: () => {
          const itemId = sceneId ? attempt(studio, () => studio.commands.addNestedScene(sceneId, id)) : undefined;
          if (itemId) ui.getState().select([itemId]);
        },
      };
    }),
  });
  entries.push(...groupEntries(studio, ui, actions));
  return tidyMenu(entries);
}

function duplicateDefault(studio: StudioApi, ui: UiStore, actions: Actions): void {
  const sceneId = actions.editedSceneId();
  const ids = actions.selectedIds();
  if (!sceneId || ids.length === 0) return;
  const created = attempt(studio, () => studio.commands.duplicateItems(sceneId, ids));
  if (created) ui.getState().select(created);
}

/** Context menu for the current selection; `itemId` is the item that was right-clicked. */
export function itemContextMenu(studio: StudioApi, ui: UiStore, actions: Actions, itemId: Id | null): MenuEntry[] {
  const clipboard = ui.getState().clipboard;
  const item = itemId ? actions.findItem(itemId) : null;
  if (!item) {
    return tidyMenu([
      { label: 'Add', submenu: addSourceMenu(studio, ui, actions) },
      { separator: true },
      { label: 'Paste', disabled: !clipboard.items, onSelect: () => actions.pasteItems() },
      { label: 'Select all', onSelect: () => actions.selectAll() },
    ]);
  }
  const owner = actions.filterOwnerOf(item.id);
  const many = actions.selectedIds().length > 1;
  return tidyMenu([
    item.type === 'source' ? { label: 'Properties', disabled: many, onSelect: () => actions.openProperties(item.id) } : null,
    owner ? { label: 'Filters', disabled: many, onSelect: () => actions.openFilters(item.id) } : null,
    { separator: true },
    { label: 'Transform', submenu: transformMenu(actions) },
    {
      label: 'Order',
      submenu: [
        { label: 'Move up', onSelect: () => actions.order('up') },
        { label: 'Move down', onSelect: () => actions.order('down') },
        { separator: true },
        { label: 'Move to top', onSelect: () => actions.order('top') },
        { label: 'Move to bottom', onSelect: () => actions.order('bottom') },
      ],
    },
    {
      label: 'Blend mode',
      submenu: BLEND_MODES.map((mode) => ({
        label: title(mode),
        checked: item.blendMode === mode,
        onSelect: () => actions.setBlendMode(mode),
      })),
    },
    {
      label: 'Scale filtering',
      submenu: SCALE_FILTERS.map((filter) => ({
        label: title(filter),
        checked: item.scaleFilter === filter,
        onSelect: () => actions.setScaleFilter(filter),
      })),
    },
    { separator: true },
    { label: item.visible ? 'Hide' : 'Show', onSelect: () => actions.toggleVisibility() },
    { label: item.locked ? 'Unlock' : 'Lock', onSelect: () => actions.toggleLock() },
    { separator: true },
    ...groupEntries(studio, ui, actions),
    item.type === 'group' ? { label: 'Ungroup', onSelect: () => actions.ungroup() } : null,
    {
      label: 'Duplicate',
      submenu: [
        // Each source type decides: live inputs share their device, everything else is copied.
        { label: 'Default for this source type', onSelect: () => duplicateDefault(studio, ui, actions) },
        { label: 'As reference (shared source)', onSelect: () => actions.duplicate('reference') },
        { label: 'As copy (independent source)', onSelect: () => actions.duplicate('copy') },
      ],
    },
    { separator: true },
    { label: 'Copy', onSelect: () => actions.copyItems() },
    { label: 'Paste', disabled: !clipboard.items, onSelect: () => actions.pasteItems() },
    { label: 'Copy transform', disabled: many, onSelect: () => actions.copyTransform() },
    { label: 'Paste transform', disabled: !clipboard.transform, onSelect: () => actions.pasteTransform() },
    owner ? { label: 'Copy filters', disabled: many, onSelect: () => actions.copyFilters() } : null,
    owner ? { label: 'Paste filters', disabled: !clipboard.filters, onSelect: () => actions.pasteFilters() } : null,
    { separator: true },
    { label: 'Remove', danger: true, onSelect: () => actions.removeItems() },
  ]);
}
